import React, { useState } from "react";
import { useFormContext, Controller, RegisterOptions } from "react-hook-form";
import { InputRaw } from "../InputRaw";

interface RHFInputProps {
  name: string;
  type?:
    | "text"
    | "number"
    | "color"
    | "date"
    | "datetime-local"
    | "email"
    | "hidden"
    | "month"
    | "password"
    | "time"
    | "range"
    | "search"
    | "tel"
    | "url"
    | "week";
  label?: string;
  span?: any;
  id?: string;
  decimal?: boolean;
  phone?: boolean;
  cep?: boolean;
  cpf?: boolean;
  onlyNumber?: boolean;
  rules?: RegisterOptions;
  value?: any;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  width?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onLimit?: () => void;
}

export function RHFInput({
  name,
  type,
  label,
  span,
  id,
  rules,
  decimal,
  phone,
  cep,
  cpf,
  onlyNumber,
  placeholder,
  maxLength,
  onChange,
  onBlur,
  onLimit,
  width,
  ...rest
}: RHFInputProps) {
  const { control } = useFormContext();
  const [hasExceededLimit, setHasExceededLimit] = useState(false);

  const handleChangeAndFormatNumber = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let { value } = e.target;
    value = value.replace(/\D/g, "");

    if (value) {
      value = Number(value).toLocaleString("pt-BR");
    } else {
      value = "";
    }

    e.target.value = value;
  };

  const handleChangeTelefone = (e: any) => {
    let { value } = e.target;
    if (value.length <= 14) {
      value = value
        .replace(/\D/g, "")
        .replace(/(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{4})(\d)/, "$1-$2")
        .replace(/(-\d{4})(\d+?)$/, "$1");
    } else {
      value = value
        .replace(/\D/g, "")
        .replace(/(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2")
        .replace(/(-\d{4})(\d+?)$/, "$1");
    }
    e.target.value = value;
  };

  const handleChangeCEP = (e: any) => {
    const { value } = e.target;
    e.target.value = value
      .replace(/\D/g, "")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .replace(/(-\d{3})(\d+?)$/, "$1");
  };

  const handleChangeCPF = (e: any) => {
    const { value } = e.target;
    if (value.length > 14) {
      e.target.value = value
        .replace(/\D/g, "")
        .replace(/(\d{2})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1/$2")
        .replace(/(\d{4})(\d)/, "$1-$2")
        .replace(/(-\d{2})(\d+?)$/, "$1");
    } else {
      e.target.value = value
        .replace(/\D/g, "")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1-$2")
        .replace(/(-\d{2})(\d+?)$/, "$1");
    }
  };

  const handleLimitOnlyNumber = (e: any) => {
    const { value } = e.target;
    e.target.value = value.replace(/\D/g, "");
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { ref, ...fieldProps }, fieldState: { error } }) => {
        return (
          <InputRaw
            {...fieldProps}
            width={width}
            value={rest.value || fieldProps.value || ""}
            type={type}
            label={label}
            placeholder={placeholder}
            span={span}
            id={id}
            error={error ? error.message : ""}
            onChange={(e: any) => {
              if (decimal) handleChangeAndFormatNumber(e);

              if (phone) handleChangeTelefone(e);

              if (cep) handleChangeCEP(e);

              if (cpf) handleChangeCPF(e);

              if (onlyNumber) handleLimitOnlyNumber(e);

              if (maxLength && e.target.value.length > maxLength) {
                e.target.value = e.target.value.slice(0, maxLength);

                if (!hasExceededLimit) {
                  if (onLimit) {
                    onLimit();
                  }
                  setHasExceededLimit(true);
                }
              } else if (maxLength && e.target.value.length <= maxLength) {
                setHasExceededLimit(false);
              }

              fieldProps.onChange(e);

              if (onChange) onChange(e);
            }}
            onBlur={(e: any) => {
              if (onBlur) {
                onBlur(e);
              }
            }}
            {...rest}
          />
        );
      }}
    />
  );
}
